/**
 * Live tracking, the staff live map of Teslyn Cloud (decision 842: "live tracking is for staff and needs to work like
 * the maps in Teslyn Field; the Operations Hub is for management"; docs/specs/cloud/live-tracking.md). One project at
 * a time, as Teslyn Field's Map: its boundaries and lines in their states (or the lines remaining), landholder
 * properties, hazard pins and the pilot hazard pack, the organisation's tracked aircraft and the public air traffic
 * around the project with trails and labels, fire layers, the base maps, the Operations Hub's Tracking assets list
 * with follow and fly to, and the 48-hour replay. Every layer is read and drawn by the code the Cloud's other maps
 * use (`RemainingMap`, `useProjectAircraft`, `useCloudFires`, `TrackingAssetsPanel`); the page's own rules are
 * `@teslyn/domain` `live-tracking.ts`. Choices (project, base map, layers) are this browser's own.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { cloudRouteFetcher } from '../map/publicRoute.ts';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  BOUNDARY_LEGEND,
  LINE_STATE_LEGEND,
  LIVE_HISTORY_HOURS,
  LIVE_LAYERS,
  LIVE_RECORDS_REFRESH_MS,
  LIVE_REPLAY_HOURS,
  LIVE_REPLAY_NOTE,
  REMAINING_LEGEND,
  baseMapKind,
  blockFeatures,
  hubSourceStrip,
  hubTrafficAreas,
  landholderFeatures,
  lineStateFeatures,
  linesRemaining,
  liveDefaultProject,
  liveLinesShown,
  liveProjectChoices,
  liveProjectWords,
  liveReplayAircraft,
  liveTrafficNotAskedWords,
  packWithoutPins,
  packWithoutShapes,
  shapeLayerFeatures,
  type ShapeLayerRecord,
  readLiveLayers,
  type BaseLayerId,
  type HazardPinRecord,
  type LandParcel,
  type LegendEntry,
  type LiveLayerId,
  type LiveLayers,
  type MapFeature,
  type TenantRole,
  type TrailPoint,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { CheckField, SelectField } from '../components/Fields.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import { FireControls, useCloudFires, useFireChoice } from '../map/cloudFires.tsx';
import { WEB_BASE_LAYERS } from '../map/leaflet.ts';
import { ReplayBar, useReplay } from '../map/ReplayBar.tsx';
import { TrackingAssetsPanel } from '../ops/TrackingAssetsPanel.tsx';
import { loadProjectPlan, loadProjects, type PortalProject, type ProjectPlan } from '../portal/api.ts';
import { LEGEND_ORDER } from '../portal/ProjectsScreen.tsx';
import { useProjectAircraft } from '../portal/projectTraffic.ts';
import { RemainingMap, type RemainingMapTracking } from '../portal/RemainingMap.tsx';
import {
  loadHazardPins,
  loadLandParcels,
  loadShapeLayers,
  loadStaffPack,
  type StaffPack,
} from './liveApi.ts';

const PROJECT_KEY = 'teslyn.cloud.live.project';
const LAYERS_KEY = 'teslyn.cloud.live.layers';
const BASE_KEY = 'teslyn.cloud.live.base';

function stored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function store(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage unavailable: the choice is not remembered */
  }
}

function storedLayers(): LiveLayers {
  try {
    return readLiveLayers(JSON.parse(stored(LAYERS_KEY) ?? 'null'));
  } catch {
    return readLiveLayers(null);
  }
}

function storedBase(): BaseLayerId {
  const v = stored(BASE_KEY);
  return WEB_BASE_LAYERS.find((b) => b.id === v)?.id ?? 'hybrid';
}

const errorText = (e: unknown): string => (e instanceof Error ? e.message : String(e));
const viewerOffsetH = (): number => -new Date().getTimezoneOffset() / 60;

export interface LiveTrackingScreenProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly roles: readonly TenantRole[];
  /** Now, for the trackers' ages and the replay; tests pass a fixed one. */
  readonly nowMs?: () => number;
}

export function LiveTrackingScreen({
  sb,
  tenantId,
  roles,
  nowMs = Date.now,
}: LiveTrackingScreenProps): ReactElement {
  const [projects, setProjects] = useState<PortalProject[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    setError(null);
    loadProjects(sb, { kind: 'tenant', tenantId })
      .then((p) => live && setProjects(liveProjectChoices(p)))
      .catch((e: unknown) => live && setError(errorText(e)));
    return () => {
      live = false;
    };
  }, [sb, tenantId, attempt]);
  const [chosen, setChosen] = useState<string | null>(() => stored(PROJECT_KEY));
  const [layers, setLayersState] = useState<LiveLayers>(storedLayers);
  const [base, setBaseState] = useState<BaseLayerId>(storedBase);
  const setLayers = (l: LiveLayers): void => {
    setLayersState(l);
    store(LAYERS_KEY, JSON.stringify(l));
  };
  const setBase = (b: BaseLayerId): void => {
    setBaseState(b);
    store(BASE_KEY, b);
  };
  const choose = (id: string): void => {
    setChosen(id);
    store(PROJECT_KEY, id);
  };

  const header = <PageHeader title="Live tracking" />;
  if (error)
    return (
      <section className="page page--wide live">
        {header}
        <ErrorState message={error} onRetry={() => setAttempt((n) => n + 1)} />
      </section>
    );
  if (!projects)
    return (
      <section className="page page--wide live">
        {header}
        <Loading what="the projects" />
      </section>
    );
  const id = liveDefaultProject(projects, chosen);
  const project = projects.find((p) => p.id === id) ?? null;
  if (!project)
    return (
      <section className="page page--wide live">
        {header}
        <p className="body">
          No project to show yet. Projects appear here once Teslyn Field syncs them; their aircraft, lines and
          hazards are drawn on this map.
        </p>
      </section>
    );
  return (
    <section className="page page--wide live">
      {header}
      <LiveProjectMap
        key={project.id}
        sb={sb}
        tenantId={tenantId}
        roles={roles}
        project={project}
        projects={projects}
        onProject={choose}
        layers={layers}
        onLayers={setLayers}
        base={base}
        onBase={setBase}
        nowMs={nowMs}
      />
    </section>
  );
}

/** Read `load` now and every `ms`; the last answer stays while the next is on its way. */
function useRefreshed<T>(
  load: () => Promise<T>,
  ms: number,
): { readonly value: T | null; readonly error: string | null } {
  const [value, setValue] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    load()
      .then((v) => {
        if (!live) return;
        setValue(v);
        setError(null);
      })
      .catch((e: unknown) => live && setError(errorText(e)));
    const t = setTimeout(() => setTick((n) => n + 1), ms);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [load, ms, tick]);
  return { value, error };
}

const NO_BLOCKS: ProjectPlan['blocks'] = [];

function Swatch({ entry }: { readonly entry: LegendEntry }): ReactElement {
  return (
    <span
      className={`legend__swatch legend__swatch--${entry.stroke}${entry.dashed ? ' legend__swatch--dashed' : ''}`}
      style={{ borderColor: `var(--${entry.colour})` }}
      aria-hidden="true"
    />
  );
}

function LiveProjectMap({
  sb,
  tenantId,
  roles,
  project,
  projects,
  onProject,
  layers,
  onLayers,
  base,
  onBase,
  nowMs,
}: {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly roles: readonly TenantRole[];
  readonly project: PortalProject;
  readonly projects: readonly PortalProject[];
  readonly onProject: (id: string) => void;
  readonly layers: LiveLayers;
  readonly onLayers: (l: LiveLayers) => void;
  readonly base: BaseLayerId;
  readonly onBase: (b: BaseLayerId) => void;
  readonly nowMs: () => number;
}): ReactElement {
  // a public aircraft's planned route on a click (R160; the live map is staff only)
  const routeOf = useMemo(() => cloudRouteFetcher(sb, tenantId), [sb, tenantId]);
  // ---- the project's records: plan and line states, pins, landholders, the pilot hazard pack (read again each 2 min)
  const loadPlan = useCallback(() => loadProjectPlan(sb, project.id), [sb, project.id]);
  const plan = useRefreshed(loadPlan, LIVE_RECORDS_REFRESH_MS);
  const loadPins = useCallback(() => loadHazardPins(sb, project.id), [sb, project.id]);
  const pins = useRefreshed<HazardPinRecord[]>(loadPins, LIVE_RECORDS_REFRESH_MS);
  const loadParcels = useCallback(() => loadLandParcels(sb, project.id), [sb, project.id]);
  const parcels = useRefreshed<LandParcel[]>(loadParcels, LIVE_RECORDS_REFRESH_MS);
  // shape file layers (R152, decision 921): hazards and other layers added in Teslyn Field
  const loadShapes = useCallback(() => loadShapeLayers(sb, project.id), [sb, project.id]);
  const shapes = useRefreshed<ShapeLayerRecord[]>(loadShapes, LIVE_RECORDS_REFRESH_MS);
  // the pack (up to 10 MB) is read again only when its version changed
  const packKnown = useRef<StaffPack | null>(null);
  const loadPack = useCallback(
    () =>
      loadStaffPack(sb, project.id, packKnown.current).then((p) => {
        packKnown.current = p;
        return p;
      }),
    [sb, project.id],
  );
  const pack = useRefreshed<StaffPack | null>(loadPack, LIVE_RECORDS_REFRESH_MS);

  // ---- aircraft: the organisation's own (every staff role, through the project functions) and public traffic
  const [hours, setHours] = useState(LIVE_HISTORY_HOURS);
  const [shownGroups, setShownGroups] = useState<string[]>([]);
  const [replaying, setReplaying] = useState(false);
  const blocks = plan.value?.blocks ?? NO_BLOCKS;
  const aircraft = useProjectAircraft(sb, project, blocks, true, nowMs, {
    hours: replaying ? LIVE_REPLAY_HOURS : hours,
    shownGroups,
  });
  const hub = aircraft.hub;
  // the replay plays the organisation's own aircraft and their trails only (public traffic is kept 2 hours)
  const own = useMemo(() => {
    const aircraft = liveReplayAircraft(hub?.aircraft ?? []);
    const trails = new Map<string, readonly TrailPoint[]>();
    for (const a of aircraft) {
      const t = hub?.trails.get(a.id);
      if (t) trails.set(a.id, t);
    }
    return { aircraft, trails };
  }, [hub]);
  const replay = useReplay(own.aircraft, own.trails, LIVE_REPLAY_HOURS, replaying, nowMs);

  // ---- follow and fly to (the Hub's Tracking assets list)
  const [follow, setFollow] = useState<string | null>(null);
  const [followStopped, setFollowStopped] = useState<string | null>(null);
  const [flyTo, setFlyTo] = useState<{ id: string; nonce: number } | null>(null);

  // ---- fire (decision 806): off until ticked, the Hub's ticks
  const [fireChoice, setFireChoice] = useFireChoice();
  const fires = useCloudFires(sb, tenantId, fireChoice, nowMs);

  // ---- what the map draws
  const lines = liveLinesShown(layers);
  const drawn = useMemo(() => {
    const p = plan.value;
    const where = { epsg: project.epsg };
    const remaining = p ? linesRemaining(p.statuses, p.plan, p.blocks, where) : null;
    const out: MapFeature[] = [];
    const notDrawn: string[] = [];
    // landholder properties under the lines, so a line inside a property keeps its tooltip
    if (layers.landholders && parcels.value)
      out.push(...landholderFeatures(parcels.value, roles, false).features);
    // shape file layers under the lines, as Teslyn Field draws them
    if (layers.shapes && shapes.value) out.push(...shapeLayerFeatures(shapes.value).features);
    if (p) {
      if (layers.boundaries) {
        const b = blockFeatures(p.blocks, where);
        out.push(...b.features);
        notDrawn.push(...b.notDrawn);
      }
      if (lines === 'states') {
        const s = lineStateFeatures(p.statuses, p.plan, p.blocks, where);
        out.push(...s.features);
        notDrawn.push(...s.notDrawn);
      } else if (lines === 'remaining' && remaining) {
        out.push(...remaining.features);
        notDrawn.push(...remaining.notDrawn);
      }
    }
    return { features: out, notDrawn, text: remaining?.text ?? null };
  }, [
    plan.value,
    parcels.value,
    shapes.value,
    project.epsg,
    layers.landholders,
    layers.shapes,
    layers.boundaries,
    lines,
    roles,
  ]);
  // a shape file layer the pack holds is drawn from its record, once
  const hazards = useMemo(
    () =>
      layers.pack && pack.value
        ? packWithoutShapes(packWithoutPins(pack.value.layers, pins.value ?? []), shapes.value ?? [])
        : [],
    [layers.pack, pack.value, pins.value, shapes.value],
  );
  const shownPins = layers.pins ? (pins.value ?? []) : [];
  const tracking = useMemo<RemainingMapTracking | null>(() => {
    if (replay) {
      if (!replay.frame || !hub || !layers.aircraft) return null;
      return { aircraft: replay.frame.aircraft, trails: replay.frame.trails, colours: hub.colours };
    }
    const t = aircraft.tracking;
    if (!t) return null;
    const kept = t.aircraft.filter((a) => (a.fleet === false ? layers.traffic : layers.aircraft));
    return kept.length > 0 ? { ...t, aircraft: kept, ...(layers.traffic ? {} : { credits: [] }) } : null;
  }, [replay, hub, aircraft.tracking, layers.aircraft, layers.traffic]);

  // ---- the public traffic sources (the Hub's strip) for this project
  const areas = useMemo(
    () =>
      hubTrafficAreas([
        {
          id: project.id,
          number: project.number,
          status: project.status,
          epsg: project.epsg,
          blocks,
          contractAreas: null,
        },
      ]),
    [project.id, project.number, project.status, project.epsg, blocks],
  );
  const traffic = aircraft.traffic;
  const sources = useMemo(
    () =>
      traffic?.status
        ? hubSourceStrip({ status: traffic.status, rows: traffic.rows, areas, nowMs: nowMs() })
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- now is read at each refresh of the traffic
    [traffic, areas],
  );
  const notAsked = liveTrafficNotAskedWords(project.status);

  const [layersOpen, setLayersOpen] = useState(false);
  const tick = (id: LiveLayerId, v: boolean): void => onLayers({ ...layers, [id]: v });
  const offsetH = viewerOffsetH();
  const loading =
    !plan.value && !plan.error
      ? 'Loading the project…'
      : plan.error
        ? `Could not load the plan: ${plan.error}`
        : null;

  return (
    <>
      <div className="live-bar" role="group" aria-label="Map controls">
        <SelectField
          label="Project"
          value={project.id}
          onChange={onProject}
          options={projects.map((p) => [p.id, liveProjectWords(p)] as const)}
        />
        <SelectField
          label="Base map"
          value={base}
          onChange={onBase}
          options={WEB_BASE_LAYERS.map((b) => [b.id, b.label] as const)}
        />
        <div className="live-bar__buttons">
          <Button
            aria-expanded={layersOpen}
            aria-controls="live-layers"
            onClick={() => setLayersOpen((o) => !o)}
          >
            Layers
          </Button>
          <Button aria-pressed={replaying} onClick={() => setReplaying((r) => !r)}>
            {replaying ? 'Back to live' : 'Replay'}
          </Button>
        </div>
      </div>
      {drawn.text && <p className="heading live__progress">{drawn.text}</p>}
      {loading && (
        <p className="caption muted" role="status">
          {loading}
        </p>
      )}
      <div className="live-body">
        <div className="live-stage" data-basemap={baseMapKind(base)}>
          {replay && (
            <ReplayBar
              replay={replay}
              offsetH={offsetH}
              note={LIVE_REPLAY_NOTE}
              onClose={() => setReplaying(false)}
            />
          )}
          <RemainingMap
            className="live-map"
            features={drawn.features}
            label={`Live map of ${project.number} ${project.title}`}
            base={base}
            hazards={hazards}
            hazardsOver
            pins={shownPins}
            identify
            fitKey={project.id}
            tracking={tracking}
            overlay={fires.layers.features}
            credits={fires.layers.credits}
            follow={follow}
            onFollowStopped={() => {
              setFollowStopped(tracking?.aircraft.find((a) => a.id === follow)?.label ?? null);
              setFollow(null);
            }}
            flyTo={flyTo}
            routeOf={routeOf}
          />
          {layersOpen && (
            <div id="live-layers" className="live-layers" role="region" aria-label="Map layers">
              <div className="live-layers__head">
                <h2 className="label">Map layers</h2>
                <Button variant="quiet" onClick={() => setLayersOpen(false)}>
                  Close
                </Button>
              </div>
              <fieldset className="live-layers__ticks">
                <legend className="caption muted">What the map shows</legend>
                {LIVE_LAYERS.map((l) => (
                  <div key={l.id} className="live-layers__tick">
                    <CheckField label={l.word} checked={layers[l.id]} onChange={(v) => tick(l.id, v)} />
                    {l.note && <p className="caption muted">{l.note}</p>}
                  </div>
                ))}
              </fieldset>
              {(fires.answer || fires.error) && (
                <FireControls fires={fires} choice={fireChoice} onChoice={setFireChoice} />
              )}
              {pack.value && (
                <p className="caption muted">
                  Pilot hazard pack "{pack.value.name}", version {pack.value.version}.
                </p>
              )}
            </div>
          )}
        </div>
        <TrackingAssetsPanel
          list={hub?.list ?? null}
          aircraft={hub?.aircraft ?? []}
          colours={hub?.colours ?? new Map()}
          groups={hub?.groups ?? []}
          shownGroups={shownGroups}
          onGroups={setShownGroups}
          hours={replaying ? LIVE_REPLAY_HOURS : hours}
          onHours={setHours}
          follow={follow}
          onFollow={(a) => {
            setFollow(a);
            setFollowStopped(null);
          }}
          onLocate={(a) => setFlyTo((f) => ({ id: a, nonce: (f?.nonce ?? 0) + 1 }))}
          status={aircraft.status}
          followStopped={followStopped}
          accounts={[]}
          traffic={aircraft.text}
        />
      </div>
      <ul className="legend" aria-label="Legend">
        {layers.boundaries && blocks.length > 0 && (
          <li className="legend__item caption">
            <Swatch entry={BOUNDARY_LEGEND} />
            {BOUNDARY_LEGEND.word}
          </li>
        )}
        {lines === 'states' &&
          LINE_STATE_LEGEND.map((e) => (
            <li key={e.word} className="legend__item caption">
              <Swatch entry={e} />
              {e.word}
            </li>
          ))}
        {lines === 'remaining' &&
          LEGEND_ORDER.map((s) => (
            <li key={s} className="legend__item caption">
              <Swatch entry={REMAINING_LEGEND[s]} />
              {REMAINING_LEGEND[s].word}
            </li>
          ))}
      </ul>
      {layers.traffic && notAsked && <p className="caption muted">{notAsked}</p>}
      {layers.traffic && sources && !notAsked && (
        <div className="hub-sources live-sources" role="group" aria-label="Public air traffic sources">
          <p className="caption hub-sources__total">
            Public aircraft on the map: <span className="data">{hub?.publicCount ?? 0}</span>
          </p>
          <ul className="hub-sources__list">
            {sources.map((i) => (
              <li
                key={i.service}
                className={`caption hub-sources__item hub-sources__item--${i.state}`}
                title={i.title}
                aria-label={`${i.name}: ${i.word}, ${i.aircraft} aircraft${i.age ? `, ${i.age}` : ''}. ${i.title}`}
              >
                <span className="hub-sources__dot" aria-hidden="true" />
                <strong>{i.name}</strong> {i.word} · <span className="data">{i.aircraft}</span>
                {i.age && <span className="muted"> · {i.age}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {(pins.error || parcels.error || pack.error || shapes.error) && (
        <p className="caption muted" role="status">
          {[
            pins.error && `The hazard pins could not be loaded: ${pins.error}`,
            parcels.error && `The landholder properties could not be loaded: ${parcels.error}`,
            shapes.error && `The shape file layers could not be loaded: ${shapes.error}`,
            pack.error && `The pilot hazard pack could not be loaded: ${pack.error}`,
          ]
            .filter(Boolean)
            .join(' ')}
        </p>
      )}
      {drawn.notDrawn.length > 0 && (
        <div className="notice notice--warn" role="status">
          <p className="label">Not drawn</p>
          <ul>
            {drawn.notDrawn.map((n) => (
              <li key={n} className="body">
                {n}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}
